"use client";

import type { VenuePageCtaBanner } from "@punchup/data/models/venue_pages";
import type { VenuePagesRow } from "@punchup/data";
import { FormFieldStatus } from "@punchup/ui-common/components/common";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { updateVenueCtaBanner } from "../../data/venue_pages";
import { useState } from "react";

interface Props {
  venuePage: VenuePagesRow;
}

const DEFAULT_BANNER: VenuePageCtaBanner = {
  enabled: true,
  text: "",
  url: "",
  background_color: "#6366f1",
};

function parseBanner(raw: unknown): VenuePageCtaBanner {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_BANNER, enabled: false };
  }
  const obj = raw as Record<string, unknown>;
  if (
    typeof obj.enabled !== "boolean" ||
    typeof obj.text !== "string" ||
    typeof obj.url !== "string"
  ) {
    return { ...DEFAULT_BANNER, enabled: false };
  }
  return {
    enabled: obj.enabled,
    text: obj.text,
    url: obj.url,
    background_color:
      typeof obj.background_color === "string"
        ? obj.background_color
        : DEFAULT_BANNER.background_color,
  };
}

export default function VenueCtaBannerSettings({ venuePage }: Props) {
  const initial = parseBanner(venuePage.cta_banner);
  const [banner, setBanner] = useState<VenuePageCtaBanner>(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supabase = createClientComponentClient();

  const handleSave = async () => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      const payload = banner.enabled ? banner : null;
      await updateVenueCtaBanner({
        supabase,
        venuePageId: venuePage.id,
        newCtaBanner: payload,
      });
      setSuccess(true);
    } catch (err) {
      console.error("[VenueCtaBannerSettings] Error:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <label className="flex items-center gap-2 text-sm font-medium text-secondary-blue">
        <input
          type="checkbox"
          checked={banner.enabled}
          onChange={(e) =>
            setBanner((prev) => ({ ...prev, enabled: e.target.checked }))
          }
          className="h-4 w-4 rounded border-gray-300"
        />
        Enable CTA Banner
      </label>

      {banner.enabled && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="cta-text"
              className="text-sm font-medium text-secondary-blue"
            >
              Button Text
            </label>
            <input
              id="cta-text"
              type="text"
              value={banner.text}
              onChange={(e) =>
                setBanner((prev) => ({ ...prev, text: e.target.value }))
              }
              placeholder="e.g. Buy Tickets Now"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="cta-url"
              className="text-sm font-medium text-secondary-blue"
            >
              Link URL
            </label>
            <input
              id="cta-url"
              type="url"
              value={banner.url}
              onChange={(e) =>
                setBanner((prev) => ({ ...prev, url: e.target.value }))
              }
              placeholder="https://..."
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="cta-color"
              className="text-sm font-medium text-secondary-blue"
            >
              Background Color
            </label>
            <div className="flex items-center gap-2">
              <input
                id="cta-color"
                type="color"
                value={banner.background_color ?? "#6366f1"}
                onChange={(e) =>
                  setBanner((prev) => ({
                    ...prev,
                    background_color: e.target.value,
                  }))
                }
                className="h-9 w-9 cursor-pointer rounded border border-gray-300 p-0.5"
              />
              <span className="text-sm text-secondary-blue">
                {banner.background_color ?? "#6366f1"}
              </span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={loading}
        className="self-start rounded-md bg-secondary-blue px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {loading ? "Saving..." : "Save"}
      </button>

      <FormFieldStatus
        loading={loading}
        loadingMessage="Updating CTA banner..."
        success={success}
        successMessage="CTA banner updated!"
        error={Boolean(error)}
        errorMessage={error ?? ""}
      />
    </div>
  );
}
